import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import CheckCircle from '../svg/CheckCircle';
import AlertCircle from '../svg/AlertCirle';
import ArrowRight from '../svg/ArrowRight';
import InvalidElement from '../../../domain/Folder/InvalidElementInterface';
import { useHistory, useParams } from 'react-router';
import { ERole, roleBackOfficeList } from '../../../domain/User/Erole';
import Cross from '../svg/Cross';
import Page from '../../../domain/Page/Page';
import FindAllBlocksUseCase from '../../../domain/Block/UseCase/FindAllBlocksUseCase';
import FetchBlockGateway from '../../../gateway/Block/FetchBlockGateway';
import FetchFolderGateway from '../../../gateway/Folder/FetchFolderGateway';
import { EModule } from '../../../domain/User/Emodule';
import { useFolderId } from '../../../infrastructure/hooks';
import storage from '../../util/storage';
import PageInterface from '../../../domain/Page/PageInterface';
import { EFolderStatus } from '../../../domain/Folder/EFolderStatus';
import Forms from '../../../domain/Form/Forms';
import FormGateway from '../../../gateway/Form/FormGateway';

interface Params {
  pageId: string
  blockId: string
}

interface FooterCorrectionProps {
  pages: Page[]
  invalidElements: InvalidElement[];
}

const FooterCorrection = ({ pages, invalidElements }: FooterCorrectionProps): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory()
  const params = useParams<Params>();
  const { folderId, isFromUrl } = useFolderId();
  const [close, setClose] = useState<boolean>(false);

  const role = storage.getRole()
  const userProfile = storage.getUserInformation()
  const toReSign = storage.getToReSign(folderId)
  const [forms, setForms] = useState<Forms[]>([])
   const unableToCorrect = storage.getUnableToCorrect(folderId)

  // Build base path with folderId if available from URL
  const basePath = isFromUrl && folderId ? `/dossier/${folderId}/form` : '/form'

  useEffect(() => {
    const blockId = params.blockId;
    const index = invalidElements.findIndex(element => element.blockId === blockId) + 1
    if (index === invalidElements.length) {
      setClose(true)
    }
  }, [])

  useEffect(() => {
    new FormGateway().getForms().then(forms => {
      if (null !== forms) {
        setForms(forms)
      }
    })
  }, [])

  const nextCorrection = () => {
    if (role && roleBackOfficeList.includes(role)) {
      const blockId = params.blockId;
      const index = invalidElements.findIndex(element => element.blockId === blockId) + 1
      if (index === invalidElements.length - 1) {
        setClose(true)
      }
      history.push(`${basePath}/${invalidElements[index].pageId}/${invalidElements[index].blockId}`)
    } else {
      history.push(`${basePath}/${invalidElements[0].pageId}/${invalidElements[0].blockId}`)
    }
  }

  const goToSignBlock =  async() => {
    if (pages) {
      const findAllBlocksUseCase = new FindAllBlocksUseCase(new FetchBlockGateway(folderId))

      let targetedPage: PageInterface|null = null
      let signatureBlock = await findAllBlocksUseCase.execute(pages[pages.length - 2].id).then((blocks) => {
        if (blocks) {
                    const signatureBlock = blocks.find(block => (block.actionName === 'status_pre_signature_validation'));
          return signatureBlock
        }
      })
      if (!signatureBlock) {
        signatureBlock = await findAllBlocksUseCase.execute(pages[pages.length - 1].id).then((blocks) => {
          if (blocks) {
            const signatureBlock = blocks.find(block => (block.slug === 'signature' || block.slug === 'pre_signature'));
            return signatureBlock
          }
        })
        targetedPage = pages[pages.length - 1]
      } else {
        targetedPage = pages[pages.length - 2]
      }
      if (signatureBlock) {
        const isActiveBlock = history.location.pathname.indexOf(`${basePath}/${targetedPage.id}/${signatureBlock.id}`) !== -1
        history.push(`${basePath}/${targetedPage.id}/${signatureBlock.id}?autosubmit=1`)
        if (isActiveBlock) {
          window.location.reload()
        }
      }
    }
  }

  const askSubscriberToCorrect = () => {
      const folderId = storage.getFolderId() ?? ''
      new FetchFolderGateway().askSubscriberToCorrect(folderId)
        .then(() => {
          history.push('/dossiers/correction')
        })
        .catch((e) => {
          console.error(e)
        })
    }

  const count = Object.keys(invalidElements).length;
  const isAllValid = count === 0;
  const baseButtonClasses = "button button--medium u-pxb";
  const statusTextClasses = isAllValid ? 'text-green-600' : 'text-red-600';

  if (!forms?.length || ![EFolderStatus.Ongoing.toString(), EFolderStatus.Signed.toString()].includes(storage.getDossierState())) {
    return <></>
  }
  const currentFormId = storage.getFormId(folderId)
  const completnessType = forms.find(form => form.id === currentFormId)?.completeness_type;  
  return (
    <div id="footer-bg" className="custom-floating-footer">
      <div className="custom-floating-footer-inner u-pxm u-pyb u-mbs">
        <div id="status-text" className={`text-sm font-medium ${statusTextClasses}`}>
          {isAllValid ? (
            <span className="flex items-center gap-2 font-600">
              <CheckCircle color='var(--success)' />
              {t('validation.subscription-correction-completed')}
            </span>
          ) : (
            <span className="flex items-center gap-2 font-600">
              <AlertCircle />
              {t('validation.subscription-correction-count', { count: count })}
            </span>
          )}
        </div>

        <div className="flex gap-3 items-center">
          {count > 0 ? (
            role && !roleBackOfficeList.includes(role) && !(role === ERole.Manager && userProfile?.modules?.includes(EModule.PlatformValidator)) ? (
              <div className='row gap-3'>
                <a className='btn-next-validation content-center' onClick={() => nextCorrection()}>
                  <ArrowRight color='var(--ink-1)' />
                </a>
              </div>
            ) : (
              <>
              {role === ERole.Manager && userProfile?.modules?.includes(EModule.PlatformValidator) && unableToCorrect &&
                  <div className='row u-pxb'>
                    <button className="button button-primary button--medium u-mxAuto" onClick={() => askSubscriberToCorrect()}>
                      {t('button.ask-corrections-to-subscriber')}
                    </button>
                  </div>
                }
                {close ?
                  <a className='btn-next-validation content-center' title='Fermer' onClick={() => history.push('/dossiers/correction')}>
                    <Cross color='var(--ink-1)' />
                  </a>
                  :
                  <a className='btn-next-validation content-center' onClick={() => nextCorrection()}>
                    <ArrowRight color='var(--ink-1)' />
                  </a>
                }
              </>
            )
          ) : (
            (toReSign || 'validation' === completnessType) ?
              <div onClick={goToSignBlock}>
                <a className={`${baseButtonClasses} button-success`}>
                  {'validation' === completnessType ? t('button.send_validation') : t('button.sign')}
                </a>
              </div>
              :
              <a className='btn-next-validation content-center' title='Fermer' onClick={() => history.push('/dossiers/correction')}>
                <Cross color='var(--ink-1)' />
              </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default FooterCorrection;
